import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {getIntl, history} from 'umi';
import {NAV_SHORTCUT_DEFS} from '@/constants/shortcuts';

export type ShortcutEntry = {
  id: string;
  key: string | [string, string];
  label: string;
  action: () => void;
};

type KeyboardShortcutsContextType = {
  isHintMode: boolean;
  /** First key pressed while in hint mode; null means show all shortcuts. */
  hintFilter: string | null;
  /** Second key of a confirmed sequence, highlighted briefly before closing. */
  hintFilter2: string | null;
  toggleHintMode: () => void;
  registerShortcut: (entry: ShortcutEntry) => void;
  unregisterShortcut: (id: string) => void;
};

const SUPPRESSED_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);
const SEQUENCE_TIMEOUT_MS = 2500;

function isInFormElement(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  if (SUPPRESSED_TAGS.has(el.tagName)) return true;
  return el.isContentEditable;
}

function normalizeKey(key: string = ""): string {
  return key.length === 1 ? key.toLowerCase() : key;
}

const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextType | null>(null);

export const KeyboardShortcutsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isHintMode, setIsHintMode] = useState(false);
  const [hintFilter, setHintFilter] = useState<string | null>(null);
  const [hintFilter2, setHintFilter2] = useState<string | null>(null);
  const shortcutsRef = useRef<Map<string, ShortcutEntry>>(new Map());
  const pendingFirstKeyRef = useRef<string | null>(null);
  const sequenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingActionRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const registerShortcut = useCallback((entry: ShortcutEntry) => {
    shortcutsRef.current.set(entry.id, entry);
  }, []);

  const unregisterShortcut = useCallback((id: string) => {
    shortcutsRef.current.delete(id);
  }, []);

  const clearPending = useCallback(() => {
    pendingFirstKeyRef.current = null;
    if (sequenceTimeoutRef.current) clearTimeout(sequenceTimeoutRef.current);
  }, []);

  const closeHintMode = useCallback(() => {
    clearPending();
    if (pendingActionRef.current) clearTimeout(pendingActionRef.current);
    setIsHintMode(false);
    setHintFilter(null);
    setHintFilter2(null);
  }, [clearPending]);

  const toggleHintMode = useCallback(() => {
    clearPending();
    setHintFilter(null);
    setIsHintMode((prev) => !prev);
  }, [clearPending]);

  // Global navigation shortcuts
  useEffect(() => {
    const t = (id: string) => getIntl().formatMessage({ id });
    const nav: ShortcutEntry[] = NAV_SHORTCUT_DEFS.map(({ id, key, labelId, path }) => ({
      id,
      key,
      label: t(labelId),
      action: () => history.push(path),
    }));
    nav.forEach((s) => registerShortcut(s));
    return () => nav.forEach((s) => unregisterShortcut(s.id));
  }, [registerShortcut, unregisterShortcut]);

  // Dismiss hint mode on route change
  useEffect(() => {
    return history.listen(() => closeHintMode());
  }, [closeHintMode]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const key = normalizeKey(e.key);

      // '?' toggles hint mode — skip when typing in a form element
      if (key === '?') {
        if (isInFormElement()) return;
        e.preventDefault();
        toggleHintMode();
        return;
      }

      // Escape closes hint mode
      if (key === 'Escape' && isHintMode) {
        e.preventDefault();
        closeHintMode();
        return;
      }

      // While hint mode is open, intercept keys to filter/navigate shortcuts
      if (isHintMode) {
        if (isInFormElement()) return;

        const shortcuts = Array.from(shortcutsRef.current.values());

        // Second key of a sequence started inside hint mode
        if (pendingFirstKeyRef.current !== null) {
          const first = pendingFirstKeyRef.current;
          clearPending();

          const match = shortcuts.find(
            (s) =>
              Array.isArray(s.key) &&
              normalizeKey(s.key[0]) === first &&
              normalizeKey(s.key[1]) === key,
          );
          if (match) {
            e.preventDefault();
            // Briefly highlight the second key before closing
            setHintFilter2(key);
            pendingActionRef.current = setTimeout(() => {
              closeHintMode();
              match.action();
            }, 180);
          } else {
            closeHintMode();
          }
          return;
        }

        // Single-key match → execute and close
        const single = shortcuts.find((s) => !Array.isArray(s.key) && normalizeKey(s.key) === key);
        if (single) {
          e.preventDefault();
          closeHintMode();
          single.action();
          return;
        }

        // First key of a sequence → filter displayed shortcuts
        const startsSeq = shortcuts.some((s) => Array.isArray(s.key) && normalizeKey(s.key[0]) === key);
        if (startsSeq) {
          e.preventDefault();
          setHintFilter(key);
          pendingFirstKeyRef.current = key;
          sequenceTimeoutRef.current = setTimeout(() => {
            pendingFirstKeyRef.current = null;
            setHintFilter(null);
          }, SEQUENCE_TIMEOUT_MS);
          return;
        }

        // Key matches nothing → close hint mode
        closeHintMode();
        return;
      }

      if (isInFormElement()) return;

      const shortcuts = Array.from(shortcutsRef.current.values());

      // Second key of a pending sequence (normal mode)
      if (pendingFirstKeyRef.current !== null) {
        const first = pendingFirstKeyRef.current;
        clearPending();

        const match = shortcuts.find(
          (s) =>
            Array.isArray(s.key) &&
            normalizeKey(s.key[0]) === first &&
            normalizeKey(s.key[1]) === key,
        );
        if (match) {
          e.preventDefault();
          match.action();
        }
        return;
      }

      // Single-key match
      const single = shortcuts.find((s) => !Array.isArray(s.key) && normalizeKey(s.key) === key);
      if (single) {
        e.preventDefault();
        single.action();
        return;
      }

      // First key of a sequence
      const startsSeq = shortcuts.some((s) => Array.isArray(s.key) && normalizeKey(s.key[0]) === key);
      if (startsSeq) {
        pendingFirstKeyRef.current = key;
        sequenceTimeoutRef.current = setTimeout(() => {
          pendingFirstKeyRef.current = null;
        }, SEQUENCE_TIMEOUT_MS);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (sequenceTimeoutRef.current) clearTimeout(sequenceTimeoutRef.current);
    };
  }, [isHintMode, toggleHintMode, closeHintMode, clearPending]);

  return (
    <KeyboardShortcutsContext.Provider value={{ isHintMode, hintFilter, hintFilter2, toggleHintMode, registerShortcut, unregisterShortcut }}>
      {children}
    </KeyboardShortcutsContext.Provider>
  );
};

export const useKeyboardShortcuts = (): KeyboardShortcutsContextType => {
  const ctx = useContext(KeyboardShortcutsContext);
  if (!ctx) throw new Error('useKeyboardShortcuts must be used within a KeyboardShortcutsProvider');
  return ctx;
};

/**
 * Register a keyboard shortcut without a visual hint element.
 * For shortcuts tied to a visible button, use <ShortcutHint> instead.
 */
export function useShortcut(
  id: string,
  key: string | [string, string],
  label: string,
  action: () => void,
): void {
  const { registerShortcut, unregisterShortcut } = useKeyboardShortcuts();
  const actionRef = useRef(action);
  actionRef.current = action;

  useEffect(() => {
    registerShortcut({ id, key, label, action: () => actionRef.current() });
    return () => unregisterShortcut(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, JSON.stringify(key), label, registerShortcut, unregisterShortcut]);
}
